import React from "react";
import dayjs from "../../../../../utils/dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle, faClock, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

const TaskCardApproved = ({ task, currentUserId, onSelect }) => {
  const taskItems = Array.isArray(task.task_list) ? task.task_list.slice(0, 3) : [];
  const taskCount = Array.isArray(task.task_list) ? task.task_list.length : 0;

  const today = dayjs().tz().startOf("day");
  const workDate = task.work_day
    ? dayjs(task.work_day).tz().startOf("day")
    : dayjs.invalid();

  const diffDays = workDate.diff(today, "day");
  const formattedDate = workDate.isValid() ? workDate.format("DD-MMM-YYYY") : "-";
  const label = `Đã duyệt ${taskCount} việc ngày ${formattedDate}`;

  let tagText = "";
  let tagColor = "";
  if (diffDays > 0) {
    tagText = `Còn ${diffDays} ngày`;
    tagColor = "bg-green-100 text-green-700 border border-green-300";
  } else if (diffDays === 0) {
    tagText = "Đến hạn hôm nay";
    tagColor = "bg-yellow-100 text-yellow-700 border border-yellow-300";
  } else {
    tagText = `Quá hạn ${Math.abs(diffDays)} ngày`;
    tagColor = "bg-red-100 text-red-700 border border-red-300";
  }

  // 🧮 Đếm số lượng việc được giao (do sếp giao)
  const assignedCount = Array.isArray(task.task_list)
  ? task.task_list.filter(
      (t) => typeof t === "object" && t.task_type === "assigned"
    ).length
  : 0;

  const avatar = task.avatar_url ? (
    <img src={task.avatar_url} alt="avatar" className="w-8 h-8 rounded-full mr-2 object-cover" />
  ) : (
    <div className="w-8 h-8 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
      <FontAwesomeIcon icon={faUserCircle} className="text-gray-400 text-xl" />
    </div>
  );

  let cardBg = "bg-white";
  if (diffDays < 0) cardBg = "bg-red-50";
  else if (diffDays === 0) cardBg = "bg-yellow-50";



  return (
    <div
      onClick={() => {
        onSelect?.({
          employee_id: task.employee_id,
          employee_name: task.employee_name,
          branch_name: task.branch_name,
          work_day: dayjs(task.work_day).tz().format("YYYY-MM-DD"),
          task_list: task.task_list,
        });
      }}
      className={`group relative w-full border rounded-2xl p-4 shadow-sm 
        hover:shadow-xl hover:border-green-400 hover:-translate-y-1
        active:scale-[0.98] transition-all duration-300 cursor-pointer 
        flex flex-col min-h-[180px] ${cardBg} overflow-hidden`}
    >
      {/* 🏷️ Hệ thống Badge & Ribbon 3D */}
      <div className="absolute top-0 right-0 z-10 flex flex-col items-end">
        {/* Ribbon Đã Duyệt */}
        <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 text-white text-[10px] font-black tracking-widest px-3 py-1.5 rounded-bl-xl shadow-sm flex items-center gap-1.5 uppercase">
          <div className="absolute inset-0 bg-black/5 rounded-bl-xl pointer-events-none"></div>
          <FontAwesomeIcon icon={faCheckCircle} className="text-[11px]" />
          <span>Đã duyệt</span>
        </div>
        
        {/* Mép gấp 3D nhỏ cho Ribbon */}
        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-t-[6px] border-t-emerald-700"></div>
  
        {/* Badge Số việc được giao thêm */}
        {assignedCount > 0 && (
          <div className="mt-1 mr-1 flex items-center gap-1 bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-md border border-red-100 shadow-sm animate-bounce-subtle">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
            </span>
            +{assignedCount} việc mới
          </div>
        )}
      </div>
  
      {/* 🕒 Tag Thời gian (Top Left) */}
      <div className={`absolute top-3 left-3 flex items-center gap-1.5 px-2 py-0.5 rounded-lg font-bold text-[10px] uppercase tracking-wider ${tagColor} bg-opacity-10 border border-current/20`}>
        <FontAwesomeIcon icon={faClock} className="text-[9px]" />
        {tagText}
      </div>
  
      {/* 🧑 Header: Avatar & Info */}
      <div className="flex items-center gap-3 mb-4 mt-7">
        <div className="ring-2 ring-white shadow-sm rounded-full overflow-hidden">
          {avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-gray-800 text-sm truncate group-hover:text-red-600 transition-colors">
            {task.employee_name}
          </div>
          <div className="text-[11px] text-gray-500 font-medium truncate uppercase tracking-tighter opacity-80">
            {task.department_name || task.branch_name || "—"}
          </div>
        </div>
      </div>
  
      {/* 📋 Danh sách công việc (Clean Style) */}
      <div className="flex-1 space-y-1.5 pl-1 mb-3">
        {taskItems.length > 0 ? (
          taskItems.slice(0, 3).map((work, i) => (
            <div key={i} className="flex items-start gap-2 text-[13px] text-gray-600 group/item" title={work.taskname}>
              <div className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 group-hover/item:scale-150 transition-transform"></div>
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap italic font-medium">
                {work.taskname}
              </span>
            </div>
          ))
        ) : (
          <div className="italic text-gray-400 text-xs py-1">Không có công việc</div>
        )}
        {taskItems.length > 3 && (
          <div className="text-[10px] text-gray-600 font-bold ml-3 italic">
            + {taskItems.length - 3} đầu việc khác...
          </div>
        )}
      </div>
  
      {/* 📅 Footer: Tags & Label */}
      <div className="mt-auto pt-3 border-t border-gray-50 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {task.tags?.slice(0, 2).map((tag) => (
            <span key={tag.tag_id} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200">
              #{tag.tag_name}
            </span>
          ))}
        </div>
        <div className="text-[10px] italic font-medium text-gray-400">{label}</div>
      </div>
    </div>
  );

};

export default TaskCardApproved;
